/**
 * Toasts — ce qui s'est passé, dit en passant (lot 4 du catalogue, 01-11-2026).
 *
 * La pilule « ✓ Synchronisation terminée » validée dans la famille ventes, pour tout : une
 * pilule de verre qui descend du haut de l'écran, l'icône dans un rond de sa couleur de SENS
 * qui luit (`vert` fait, `rouge` raté, `ambre` attention, `gris` info, `marque`), un texte,
 * parfois un sous-texte et une action. Elle repart seule après `duree` (4 s ; `0` = elle reste,
 * avec sa croix). Cinq au plus à l'écran.
 *
 * Aucun provider, aucune plomberie : un petit magasin de module. On POSE `<Toasts />` une fois à
 * la racine de l'app, et on APPELLE `toaster()` de n'importe où — un composant, un gestionnaire
 * d'erreur, une réponse d'API.
 *
 *   <Toasts />                                      ← une fois, à la racine
 *   toaster({ texte: "Synchronisation terminée" });              vert, 4 s
 *   toaster({ texte: "Envoi raté", sous: e.message, ton: "rouge", duree: 0 });
 *   toaster({ texte: "12 comptes archivés", action: { label: "Annuler", onClick: defaire } });
 *
 * `Toast` seul, c'est la pilule posée (le catalogue, un harnais) ; `useToasts()` lit la file.
 */
import { useEffect, useSyncExternalStore, type ReactNode } from "react";
import { AnimatePresence, motion } from "framer-motion";

import { Button } from "./Button";
import { AlertTriangle, Check, Info, X } from "./icons";
import { useLibelles } from "./libelles";
import { cn } from "./utils";
import "./ouverture.css";
import "./prevenir.css";

export type TonToast = "vert" | "rouge" | "ambre" | "gris" | "marque";

export interface Toast {
  id: string;
  texte: ReactNode;
  /** Sous le texte, gris : le détail, le message d'erreur. */
  sous?: ReactNode;
  /** Défaut `vert`. */
  ton?: TonToast;
  /** À la place de l'icône du ton. */
  icone?: ReactNode;
  /** En millisecondes. Défaut 4 000 ; `0` : il reste jusqu'à sa croix. */
  duree?: number;
  action?: { label: string; onClick: () => void };
}

/* ── Le magasin ─────────────────────────────────────────────────────────── */

const AU_PLUS = 5;
let file: Toast[] = [];
let compteur = 0;
const abonnes = new Set<() => void>();

function emettre() {
  abonnes.forEach((f) => f());
}
function abonner(f: () => void) {
  abonnes.add(f);
  return () => {
    abonnes.delete(f);
  };
}
function lire() {
  return file;
}

/** Ajoute un toast (ou remplace celui qui porte le même `id`) ; rend son id. */
export function toaster(toast: Omit<Toast, "id"> & { id?: string }): string {
  const id = toast.id ?? `toast-${++compteur}`;
  file = [...file.filter((t) => t.id !== id), { ...toast, id }].slice(-AU_PLUS);
  emettre();
  return id;
}

export function fermerLeToast(id: string) {
  if (!file.some((t) => t.id === id)) return;
  file = file.filter((t) => t.id !== id);
  emettre();
}

/** La file, pour un rendu à soi ou un test. */
export function useToasts(): Toast[] {
  return useSyncExternalStore(abonner, lire, lire);
}

/* ── La pilule ──────────────────────────────────────────────────────────── */

const ICONES: Record<TonToast, ReactNode> = {
  vert: <Check weight="bold" />,
  rouge: <X weight="bold" />,
  ambre: <AlertTriangle weight="bold" />,
  gris: <Info weight="bold" />,
  marque: <Info weight="bold" />,
};

export interface ToastProps {
  texte: ReactNode;
  sous?: ReactNode;
  ton?: TonToast;
  icone?: ReactNode;
  action?: { label: string; onClick: () => void };
  /** Rend la croix. */
  onFermer?: () => void;
  /** L'étiquette (lecteur d'écran) de la croix. Défaut « Fermer ». */
  fermerLabel?: string;
  className?: string;
}

export function Toast({ texte, sous, ton = "vert", icone, action, onFermer, fermerLabel, className }: ToastProps) {
  const libelles = useLibelles();
  return (
    <div role={ton === "rouge" ? "alert" : "status"} className={cn("pv-toast pv-pilule", className)} data-ton={ton}>
      <span className="pv-toast-icone pv-rond" aria-hidden>
        {icone ?? ICONES[ton]}
      </span>
      <span className="pv-toast-texte">
        {texte}
        {sous !== undefined && sous !== null && <span className="pv-toast-sous">{sous}</span>}
      </span>
      {action && (
        <Button variant="ghost" size="sm" className="pv-toast-action" onClick={action.onClick}>
          {action.label}
        </Button>
      )}
      {onFermer && (
        <button type="button" onClick={onFermer} aria-label={fermerLabel ?? libelles.fermer} className="ov-croix ov-rond">
          <X />
        </button>
      )}
    </div>
  );
}

/* ── La pile ────────────────────────────────────────────────────────────── */

export interface ToastsProps {
  /** `haut` (défaut, centré) ou `bas-droite`. */
  position?: "haut" | "bas-droite";
  fermerLabel?: string;
}

function ToastVivant({ toast, fermerLabel }: { toast: Toast; fermerLabel?: string }) {
  const duree = toast.duree ?? 4000;
  useEffect(() => {
    if (duree <= 0) return;
    const id = window.setTimeout(() => fermerLeToast(toast.id), duree);
    return () => window.clearTimeout(id);
  }, [toast.id, duree]);
  return (
    <motion.div
      layout
      initial={{ opacity: 0, y: -22, scale: 0.94 }}
      animate={{ opacity: 1, y: 0, scale: 1 }}
      exit={{ opacity: 0, y: -14, scale: 0.96, transition: { duration: 0.22 } }}
      transition={{ type: "spring", stiffness: 480, damping: 34 }}
      className="flex w-full justify-center"
    >
      <Toast
        texte={toast.texte}
        sous={toast.sous}
        ton={toast.ton}
        icone={toast.icone}
        action={
          toast.action
            ? {
                label: toast.action.label,
                onClick: () => {
                  toast.action?.onClick();
                  fermerLeToast(toast.id);
                },
              }
            : undefined
        }
        onFermer={duree <= 0 || toast.action ? () => fermerLeToast(toast.id) : undefined}
        fermerLabel={fermerLabel}
      />
    </motion.div>
  );
}

/** À poser UNE fois, à la racine de l'app : la pile de ce que `toaster()` envoie. */
export function Toasts({ position = "haut", fermerLabel }: ToastsProps) {
  const toasts = useToasts();
  return (
    <div className="pv-toasts" data-position={position === "haut" ? undefined : position} aria-live="polite">
      <AnimatePresence initial={false}>
        {toasts.map((t) => (
          <ToastVivant key={t.id} toast={t} fermerLabel={fermerLabel} />
        ))}
      </AnimatePresence>
    </div>
  );
}
